import {CharacterItemFragment, ItemRarity, ItemSubType, ItemType, WeaponProperty} from '../../../../generated/types';
import {uuid} from '../../../../components/utils/uuid';
import {ItemRarityNames} from '../../../../components/render-helpers/item-rarity-names';
import type {Coin, EquipmentEntry} from './character-state';
import {normalizeChoose} from './normalize-choose';
import type {AttackBonusProps, ChooseGroup, ItemProps} from './types';

export type ArmorKind = 'Light' | 'Medium' | 'Heavy' | 'Shield';

export type ItemRecord = CharacterItemFragment & {
	baseItem?: CharacterItemFragment | null;
	description?: string | null;
	parentItem?: {description?: string | null} | null;
};

export const BASE_ITEM_CHOICE = 'baseItem';

export type EquipmentItem = {
	id: string;
	title: string;
	type?: ItemType;
	subTypes: string[];
	weaponCategory?: string;
	weaponProperties: string[];
	/** свойство мастерства базового оружия */
	weaponMastery: string[];
	/** id базового оружия: по нему проверяется оружейное мастерство */
	baseWeaponId: string;
	/** выбранная основа для предметов вроде «Броня +1» */
	baseTitle?: string;
	damage?: string;
	/** урон при хвате двумя руками у оружия со свойством Универсальное */
	versatileDamage?: string;
	damageType?: string;
	range?: string;
	weight: number;
	/** стоимость в золотых: цена предмета или цена по редкости с ценой основы */
	priceInGold?: number;
	armorKind?: ArmorKind;
	armorClass?: number;
	/** минимальная Сила для тяжёлого доспеха */
	strength?: number;
	stealthDisadvantage: boolean;
	requiresAttunement: boolean;
	/** магический предмет: немагический можно опознать как магический */
	isMagic: boolean;
	/** у предмета или его общего предмета есть описание: строку можно раскрыть */
	hasDescription: boolean;
	props: ItemProps;
	choose: ChooseGroup[];
};

export type EquippedItem = {
	entry: EquipmentEntry;
	item: EquipmentItem;
	/** персонаж настроен на предмет */
	isAttuned: boolean;
	/** действует часть props.equipped: предмет надет или персонаж на него настроен */
	isWorn: boolean;
	/** бонусы к атакам этим предметом от действующих частей props */
	weaponBonus: Required<AttackBonusProps>;
};

const ARMOR_KINDS: ArmorKind[] = ['Light', 'Medium', 'Heavy', 'Shield'];
const SHIELD_ARMOR_CLASS = 2;
const COINS_PER_POUND = 50;

const firstList = <T>(...lists: (readonly T[] | null | undefined)[]): T[] => [
	...(lists.find((list) => list && list.length) || []),
];

export function toEquipmentItem(item: ItemRecord, chosenBase?: CharacterItemFragment): EquipmentItem {
	const base = chosenBase || item.baseItem || undefined;
	const subTypes = firstList<string>(chosenBase?.subType, item.subType, base?.subType);
	const choose = normalizeChoose(item.choose);
	const needsBase = choose.some((group) => group.key === BASE_ITEM_CHOICE) && !chosenBase;
	const armorKind = needsBase ? undefined : ARMOR_KINDS.find((kind) => subTypes.includes(kind));

	return {
		id: item.id,
		title: item.title,
		type: item.type || base?.type || undefined,
		subTypes,
		weaponCategory: item.weaponCategory || base?.weaponCategory || undefined,
		weaponProperties: firstList<string>(item.weaponProperty, base?.weaponProperty),
		weaponMastery: firstList<string>(item.weaponMastery, base?.weaponMastery),
		baseWeaponId: base?.id || item.id,
		baseTitle: chosenBase?.title,
		damage: item.dmg1 || base?.dmg1 || undefined,
		versatileDamage: item.dmg2 || base?.dmg2 || undefined,
		damageType: item.dmgType || base?.dmgType || undefined,
		range: item.range || base?.range || undefined,
		weight: item.weight ?? base?.weight ?? 0,
		priceInGold: itemPriceInGold(item, base),
		armorKind,
		armorClass: item.ac ?? base?.ac ?? (armorKind === 'Shield' ? SHIELD_ARMOR_CLASS : undefined),
		strength: item.strength ?? base?.strength ?? undefined,
		stealthDisadvantage: !!(item.stealth ?? base?.stealth),
		requiresAttunement: !!item.reqAttune,
		isMagic: isMagicRarity(item.rarity),
		hasDescription: !!(item.description || item.parentItem?.description),
		props: (item.props || {}) as ItemProps,
		choose,
	};
}

export const isMagicRarity = (rarity: ItemRarity | null | undefined) => !!rarity && rarity !== ItemRarity.None;

/** немагический предмет оказался магическим: один предмет из стопки заменяется найденным */
export function identifyEntry(
	equipment: EquipmentEntry[],
	entryKey: string,
	itemId: string,
	baseItemId: string | undefined,
): EquipmentEntry[] {
	const choices = baseItemId ? {[BASE_ITEM_CHOICE]: [baseItemId]} : undefined;
	return equipment.flatMap((entry) => {
		if (entry.key !== entryKey) return [entry];
		if (entry.quantity <= 1) return [{...entry, itemId, choices}];
		return [
			{...entry, quantity: entry.quantity - 1},
			{key: uuid(), itemId, quantity: 1, equipped: entry.equipped, offHand: entry.offHand, choices},
		];
	});
}

const COPPER_PER_GOLD = 100;

function itemPriceInGold(item: ItemRecord, base: CharacterItemFragment | undefined): number | undefined {
	if (item.price) return item.price / COPPER_PER_GOLD;
	const rarityPrice = item.rarity ? ItemRarityNames[item.rarity]?.price : undefined;
	if (rarityPrice) return rarityPrice + (base?.price || 0) / COPPER_PER_GOLD;
	const isMundane = !item.rarity || item.rarity === ItemRarity.None;
	return isMundane && base?.price ? base.price / COPPER_PER_GOLD : undefined;
}

export const isWeapon = (item: EquipmentItem) => item.type === ItemType.Weapon;

export const isShield = (item: EquipmentItem) => item.armorKind === 'Shield';

export const isBodyArmor = (item: EquipmentItem) => !!item.armorKind && !isShield(item);

export const hasProperty = (item: EquipmentItem, property: WeaponProperty) => item.weaponProperties.includes(property);

export const isRangedWeapon = (item: EquipmentItem) => item.subTypes.includes('Ranged');

export const isAmmunition = (item: EquipmentItem) => item.subTypes.includes(ItemSubType.Ammunition);

const CONSUMABLE_TYPES = new Set<string>([
	ItemType.Potion,
	ItemType.Scroll,
	ItemType.Poison,
	ItemType.Explosive,
	ItemType.FoodAndDrink,
]);

export type EquippableKind = {type?: string | null; subTypes: readonly string[]; isMagic: boolean};

/**
 * Предмет можно взять в руки или надеть: оружие и боеприпасы, доспехи и щиты, магические предметы,
 * которые носят, кроме расходуемых.
 */
export const isEquippableKind = ({type, subTypes, isMagic}: EquippableKind) => {
	if (type === ItemType.Weapon || type === ItemType.Armor || subTypes.includes(ItemSubType.Ammunition)) return true;
	return isMagic && !CONSUMABLE_TYPES.has(type || '');
};

export const isEquippable = (item: EquipmentItem) => isEquippableKind(item) || !!item.armorKind;

/** доспехи и щиты не складываются в стопки: надевают по одному */
export const isSeparateKind = ({type, subTypes}: Pick<EquippableKind, 'type' | 'subTypes'>) =>
	type === ItemType.Armor || ARMOR_KINDS.some((kind) => subTypes.includes(kind));

/** отдельной записью лежат доспехи и щиты, а также предметы с настройкой: настраиваются на каждый отдельно */
export const isSeparate = (item: EquipmentItem) => !!item.armorKind || item.requiresAttunement;

/** одинаковые предметы, показанные одной строкой: записи хранятся отдельно, стопка собирается при показе */
export type EquipmentStack = {
	key: string;
	item: EquipmentItem;
	/** записи стопки; у доспеха и предмета с настройкой запись всегда одна */
	entries: EquippedItem[];
	quantity: number;
};

const itemKey = ({itemId, choices}: EquipmentEntry) => `${itemId}:${JSON.stringify(choices || {})}`;

/** byItem — одна стопка на предмет; иначе используемое, неиспользуемое и взятое во вторую руку — разные строки */
export function stackEquipment(equipment: EquippedItem[], {byItem = false} = {}): EquipmentStack[] {
	const stacks = new Map<string, EquipmentStack>();
	for (const equipped of equipment) {
		const {entry, item} = equipped;
		const state = byItem ? '' : `:${entry.offHand ? 'offHand' : entry.equipped ? 'equipped' : ''}`;
		const key = isSeparate(item) ? entry.key : `${itemKey(entry)}${state}`;
		const stack = stacks.get(key);
		if (stack) {
			stack.entries.push(equipped);
			stack.quantity += entry.quantity;
		} else {
			stacks.set(key, {key, item, entries: [equipped], quantity: entry.quantity});
		}
	}
	return [...stacks.values()];
}

export const stackOffHand = (stack: EquipmentStack) =>
	stack.entries.reduce((total, {entry}) => total + (entry.offHand ? entry.quantity : 0), 0);

/** взятое во вторую руку остаётся как есть, остальное количество собирается в одну запись */
export function setStackQuantity(equipment: EquipmentEntry[], stack: EquipmentStack, quantity: number) {
	const keys = new Set(stack.entries.map(({entry}) => entry.key));
	const main = stack.entries.find(({entry}) => !entry.offHand)?.entry || {
		...stack.entries[0].entry,
		key: uuid(),
		equipped: false,
		offHand: false,
	};
	const rest = quantity - stackOffHand(stack);
	return [
		...equipment.filter((entry) => !keys.has(entry.key) || entry.offHand),
		...(rest > 0 ? [{...main, quantity: rest}] : []),
	];
}

/** во вторую руку берётся один предмет из стопки, а убранный из второй руки возвращается в стопку */
export function setOffHand(equipment: EquipmentEntry[], stack: EquipmentStack, offHand: boolean) {
	const main = stack.entries.find(({entry}) => !entry.offHand)?.entry;
	if (offHand) {
		if (!main) return equipment;
		return equipment.flatMap((entry) => {
			if (entry.key !== main.key) return [entry];
			const taken = {...entry, equipped: true, offHand: true};
			return entry.quantity > 1
				? [
						{...entry, quantity: entry.quantity - 1},
						{...taken, key: uuid(), quantity: 1},
				  ]
				: [taken];
		});
	}
	const held = stack.entries.find(({entry}) => entry.offHand)?.entry;
	if (!held) return equipment;
	return equipment.flatMap((entry) => {
		if (entry.key === held.key) return main ? [] : [{...entry, equipped: false, offHand: false}];
		return main && entry.key === main.key ? [{...entry, quantity: entry.quantity + held.quantity}] : [entry];
	});
}

export type EquipSlot = 'armor' | 'shield';

export const equipSlot = (item: EquipmentItem): EquipSlot | undefined =>
	isShield(item) ? 'shield' : isBodyArmor(item) ? 'armor' : undefined;

/** экипируются сразу все записи стопки; доспех или щит снимает надетый того же вида */
export function equipEntries(
	equipment: EquipmentEntry[],
	items: EquippedItem[],
	entryKeys: string[],
	equipped: boolean,
): EquipmentEntry[] {
	const targets = new Set(entryKeys);
	const slots = new Set(items.filter(({entry}) => targets.has(entry.key)).map(({item}) => equipSlot(item)));
	const sameSlot = new Set(
		items
			.filter(({entry, item}) => !targets.has(entry.key) && equipSlot(item) && slots.has(equipSlot(item)))
			.map(({entry}) => entry.key),
	);
	return equipment.map((entry) => {
		if (targets.has(entry.key)) return {...entry, equipped, offHand: equipped ? entry.offHand : false};
		if (equipped && sameSlot.has(entry.key)) return {...entry, equipped: false};
		return entry;
	});
}

export function equipmentWeight(equipment: EquippedItem[], coins: Record<Coin, number>): number {
	const itemsWeight = equipment.reduce((total, {entry, item}) => total + item.weight * entry.quantity, 0);
	const coinCount = Object.values(coins).reduce((total, count) => total + (count || 0), 0);
	return Math.round((itemsWeight + coinCount / COINS_PER_POUND) * 100) / 100;
}
